import {
  DOCUMENT_PREVIEW_DEFAULT_HEIGHT,
  DOCUMENT_PREVIEW_DEFAULT_TOP,
  DOCUMENT_PREVIEW_DEFAULT_WIDTH,
  DOCUMENT_PREVIEW_MIN_HEIGHT,
  DOCUMENT_PREVIEW_MIN_WIDTH,
  DOCUMENT_PREVIEW_VIEWPORT_GUTTER,
} from '../constants';

import type { ResizeHandle } from 'react-resizable';
import type { DocumentPreviewDialogLayout } from './types';

export function isSameDocumentPreviewDialogLayout(
  current: DocumentPreviewDialogLayout,
  next: DocumentPreviewDialogLayout,
): boolean {
  return (
    current.height === next.height &&
    current.width === next.width &&
    current.x === next.x &&
    current.y === next.y
  );
}

export function clampDocumentPreviewDialogLayout(
  layout: DocumentPreviewDialogLayout,
  viewportWidth = window.innerWidth,
  viewportHeight = window.innerHeight,
): DocumentPreviewDialogLayout {
  const maxWidth = Math.max(0, viewportWidth - DOCUMENT_PREVIEW_VIEWPORT_GUTTER * 2);
  const maxHeight = Math.max(0, viewportHeight - DOCUMENT_PREVIEW_VIEWPORT_GUTTER * 2);
  const width = Math.min(
    Math.max(layout.width, Math.min(DOCUMENT_PREVIEW_MIN_WIDTH, maxWidth)),
    maxWidth,
  );
  const height = Math.min(
    Math.max(layout.height, Math.min(DOCUMENT_PREVIEW_MIN_HEIGHT, maxHeight)),
    maxHeight,
  );
  const maxX = Math.max(
    DOCUMENT_PREVIEW_VIEWPORT_GUTTER,
    viewportWidth - width - DOCUMENT_PREVIEW_VIEWPORT_GUTTER,
  );
  const maxY = Math.max(
    DOCUMENT_PREVIEW_VIEWPORT_GUTTER,
    viewportHeight - height - DOCUMENT_PREVIEW_VIEWPORT_GUTTER,
  );

  return {
    height,
    width,
    x: Math.min(Math.max(layout.x, DOCUMENT_PREVIEW_VIEWPORT_GUTTER), maxX),
    y: Math.min(Math.max(layout.y, DOCUMENT_PREVIEW_VIEWPORT_GUTTER), maxY),
  };
}

export function getDocumentPreviewDialogLayout(
  viewportWidth = window.innerWidth,
  viewportHeight = window.innerHeight,
): DocumentPreviewDialogLayout {
  const y = Math.max(DOCUMENT_PREVIEW_VIEWPORT_GUTTER, DOCUMENT_PREVIEW_DEFAULT_TOP);
  const width = Math.min(
    DOCUMENT_PREVIEW_DEFAULT_WIDTH,
    viewportWidth - DOCUMENT_PREVIEW_VIEWPORT_GUTTER * 2,
  );

  return clampDocumentPreviewDialogLayout(
    {
      height: Math.min(
        DOCUMENT_PREVIEW_DEFAULT_HEIGHT,
        Math.max(0, viewportHeight - y - DOCUMENT_PREVIEW_VIEWPORT_GUTTER),
      ),
      width,
      x: Math.max(DOCUMENT_PREVIEW_VIEWPORT_GUTTER, (viewportWidth - width) / 2),
      y,
    },
    viewportWidth,
    viewportHeight,
  );
}

export function getDocumentPreviewSizeConstraints(
  layout: DocumentPreviewDialogLayout,
  viewportWidth = window.innerWidth,
  viewportHeight = window.innerHeight,
  handle?: ResizeHandle,
) {
  const maxWidth = Math.max(
    0,
    handle?.includes('w')
      ? layout.x + layout.width - DOCUMENT_PREVIEW_VIEWPORT_GUTTER
      : viewportWidth - layout.x - DOCUMENT_PREVIEW_VIEWPORT_GUTTER,
  );
  const maxHeight = Math.max(
    0,
    handle?.includes('n')
      ? layout.y + layout.height - DOCUMENT_PREVIEW_VIEWPORT_GUTTER
      : viewportHeight - layout.y - DOCUMENT_PREVIEW_VIEWPORT_GUTTER,
  );

  return {
    maxHeight,
    maxWidth,
    minHeight: Math.min(DOCUMENT_PREVIEW_MIN_HEIGHT, maxHeight),
    minWidth: Math.min(DOCUMENT_PREVIEW_MIN_WIDTH, maxWidth),
  };
}

export function getDocumentPreviewDragBounds(
  layout: DocumentPreviewDialogLayout,
  viewportWidth = window.innerWidth,
  viewportHeight = window.innerHeight,
) {
  return {
    bottom: Math.max(
      DOCUMENT_PREVIEW_VIEWPORT_GUTTER,
      viewportHeight - layout.height - DOCUMENT_PREVIEW_VIEWPORT_GUTTER,
    ),
    left: DOCUMENT_PREVIEW_VIEWPORT_GUTTER,
    right: Math.max(
      DOCUMENT_PREVIEW_VIEWPORT_GUTTER,
      viewportWidth - layout.width - DOCUMENT_PREVIEW_VIEWPORT_GUTTER,
    ),
    top: DOCUMENT_PREVIEW_VIEWPORT_GUTTER,
  };
}

export function applyDocumentPreviewResize(
  layout: DocumentPreviewDialogLayout,
  nextSize: { height: number; width: number },
  handle: ResizeHandle,
  viewportWidth = window.innerWidth,
  viewportHeight = window.innerHeight,
): DocumentPreviewDialogLayout {
  const { maxHeight, maxWidth, minHeight, minWidth } = getDocumentPreviewSizeConstraints(
    layout,
    viewportWidth,
    viewportHeight,
    handle,
  );
  const height = Math.min(Math.max(nextSize.height, minHeight), maxHeight);
  const width = Math.min(Math.max(nextSize.width, minWidth), maxWidth);

  return clampDocumentPreviewDialogLayout(
    {
      height,
      width,
      x: handle.includes('w') ? layout.x + (layout.width - width) : layout.x,
      y: handle.includes('n') ? layout.y + (layout.height - height) : layout.y,
    },
    viewportWidth,
    viewportHeight,
  );
}
